import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../../hooks/useSubscription.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { Loader } from 'lucide-react';

/**
 * Component to protect HR routes that require an active subscription
 * Redirects to /hr/subscription if no active subscription
 * HR users MUST pay for a subscription before accessing the dashboard
 */
const HRSubscriptionGuard = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const { subscription, needsSubscription, loading: subscriptionLoading, hasActiveSubscription } = useSubscription();
  const location = useLocation();

  // Don't protect the subscription route itself
  if (location.pathname === '/hr/subscription' || location.pathname === '/hr/onboarding') {
    return <>{children}</>;
  }

  // Show loading state while checking subscription
  if (authLoading || subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  // Get user type from user object
  const userType = user?.userType;

  // Only apply to HR users
  if (userType !== 'hr' && userType !== 'recruiter') {
    return <>{children}</>;
  }

  // Check if HR has valid subscription
  // Must have: status === 'active' AND plan !== 'none'
  const hasValidSubscription =
    subscription &&
    subscription.status === 'active' &&
    subscription.plan !== 'none';

  // Redirect to subscription page if HR user needs subscription
  if (!hasValidSubscription || needsSubscription) {
    console.log('🚫 HR user blocked - no active subscription:', {
      subscription,
      hasActiveSubscription,
      needsSubscription,
      redirectingTo: '/hr/subscription'
    });
    return <Navigate to="/hr/subscription" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default HRSubscriptionGuard;

